// src/components/operationCenter/widgets/WatchlistWidget.tsx
// Widget wrapper for watchlist

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  TrendingUp,
  TrendingDown,
  Plus,
  Search,
  X,
  Bookmark,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { WidgetProps, WatchlistConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useTokensChanges } from "@/hooks/tokens/compare/useCompareData";
import { useTokensCatalog } from "@/hooks/useTokensCatalog";
import { useDashboardStore } from "@/stores/dashboardStore";
import { MetricKeys } from "@/constants/metrics";

const WatchlistWidget: React.FC<WidgetProps<WatchlistConfig>> = ({
  widget,
}) => {
  const config = widget.config as WatchlistConfig;
  const updateWidgetConfig = useDashboardStore((s) => s.updateWidgetConfig);

  const { data: rows } = useTokensScreenerData();
  const { changeByTicker } = useTokensChanges();
  const { data: tokenCatalog } = useTokensCatalog();

  // Memoize tickers to avoid dependency issues
  const tickers = React.useMemo(
    () => config.tickers ?? ["BTC", "ETH", "SOL"],
    [config.tickers]
  );

  // Filter tokens that have data (similar to TradingViewWidget)
  const derivativesTickers = useMemo(() => {
    if (!Array.isArray(rows)) return new Set<string>();
    const out = new Set<string>();
    rows.forEach((row) => {
      const ticker = String(row["tokenSymbol"] ?? "").toUpperCase();
      if (!ticker) return;
      const oi = Number(row[MetricKeys.FuturesOI]);
      const frOiw = Number(row[MetricKeys.FuturesFROIW]);
      const frVolw = Number(row[MetricKeys.FuturesFRVolW]);
      if ([oi, frOiw, frVolw].some((v) => Number.isFinite(v))) {
        out.add(ticker);
      }
    });
    return out;
  }, [rows]);

  const availableTokens = useMemo(
    () =>
      (tokenCatalog ?? [])
        .map((token) => token.ticker?.toUpperCase().trim())
        .filter((ticker): ticker is string => Boolean(ticker))
        .filter((ticker) => derivativesTickers.has(ticker))
        .filter((ticker) => !tickers.includes(ticker)) // Exclude already added tokens
        .sort(),
    [tokenCatalog, derivativesTickers, tickers]
  );

  // Local state for dropdown and search
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtered tokens based on search query
  const filteredTokens = useMemo(() => {
    if (!searchQuery) return availableTokens;
    const query = searchQuery.toLowerCase();
    return availableTokens.filter((t) => t.toLowerCase().includes(query));
  }, [availableTokens, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Handle adding a token to watchlist
  const handleAddToken = useCallback(
    (newTicker: string) => {
      const updatedTickers = [...tickers, newTicker.toUpperCase()];
      updateWidgetConfig(widget.id, { tickers: updatedTickers });
      setIsDropdownOpen(false);
      setSearchQuery("");
    },
    [tickers, updateWidgetConfig, widget.id]
  );

  // Handle removing a token from watchlist
  const handleRemoveToken = useCallback(
    (tickerToRemove: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const updatedTickers = tickers.filter((t) => t !== tickerToRemove);
      updateWidgetConfig(widget.id, { tickers: updatedTickers });
    },
    [tickers, updateWidgetConfig, widget.id]
  );

  const watchlistData = React.useMemo(() => {
    if (!Array.isArray(rows)) return [];

    return tickers.map((ticker) => {
      const upperTicker = ticker.toUpperCase();
      const row = rows.find(
        (r) => String(r["tokenSymbol"] ?? "").toUpperCase() === upperTicker
      );
      const changes = changeByTicker.get(upperTicker);

      // Safely extract name as string
      const rawName = row?.["tokenName"] ?? row?.["name"];
      const name = typeof rawName === "string" ? rawName : ticker;

      // Key format from useTokensChanges: {metric_key}_{window} e.g. "price_close_1d"
      return {
        ticker: upperTicker,
        name,
        price: (row?.[MetricKeys.PriceClose] as number) ?? null,
        change1d: (changes?.[`${MetricKeys.PriceClose}_1d`] as number) ?? null,
        change7d: (changes?.[`${MetricKeys.PriceClose}_7d`] as number) ?? null,
      };
    });
  }, [rows, tickers, changeByTicker]);

  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return "—";
    if (price >= 1000) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toPrecision(4)}`;
  };

  const formatChange = (change: number | null) => {
    if (change === null) return "—";
    return `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with Title and Add Button */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        {/* Title section */}
        <div className="flex items-center gap-2">
          <Bookmark className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Watchlist
          </span>
        </div>

        {/* Add Token Button with Shine Effect */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="group relative flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-all duration-300 overflow-hidden
              bg-gradient-to-br from-cyan-500/20 via-cyan-400/10 to-blue-500/20 
              hover:from-cyan-500/30 hover:via-cyan-400/20 hover:to-blue-500/30
              border border-cyan-500/30 hover:border-cyan-400/50
              text-cyan-300 hover:text-cyan-200
              shadow-[0_0_15px_rgba(6,182,212,0.15)] hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]"
            title="Add token to watchlist"
          >
            {/* Animated shine overlay */}
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <Plus className="w-3.5 h-3.5 relative z-10" />
            <span className="relative z-10">Add</span>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Search Input */}
              <div className="p-2 border-b border-slate-700/50 bg-slate-800/50">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tokens..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-900/50 border border-slate-600 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Token List */}
              <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50">
                {filteredTokens.length > 0 ? (
                  <div className="py-1">
                    {filteredTokens.map((token) => (
                      <button
                        key={token}
                        onClick={() => handleAddToken(token)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 transition-colors"
                      >
                        <span className="font-medium">{token}</span>
                        <Plus className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-slate-500">
                    {searchQuery ? "No tokens found" : "No more tokens to add"}
                  </div>
                )}
              </div>

              {/* Footer Info */}
              <div className="px-3 py-1.5 border-t border-slate-700/50 bg-slate-800/50 text-xs text-slate-500">
                {filteredTokens.length}{" "}
                {filteredTokens.length === 1 ? "token" : "tokens"} available
              </div>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto space-y-1.5 p-3">
        {watchlistData.map((item) => {
          const isUp = (item.change1d ?? 0) >= 0;
          return (
            <Link
              key={item.ticker}
              to={`/app/tokens/${item.ticker.toLowerCase()}`}
              className="group flex items-center justify-between p-2.5 rounded-lg bg-slate-900/30 hover:bg-slate-700/30 transition-colors"
            >
              <div className="min-w-0 flex items-center gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    {item.ticker}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    {item.name}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-sm font-medium text-slate-200">
                    {formatPrice(item.price)}
                  </div>
                  <div
                    className={`flex items-center justify-end gap-1 text-xs font-medium ${
                      isUp ? "text-emerald-400" : "text-rose-400"
                    }`}
                  >
                    {isUp ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : (
                      <TrendingDown className="w-3 h-3" />
                    )}
                    {formatChange(item.change1d)}
                  </div>
                </div>
                {/* Remove button */}
                <button
                  onClick={(e) => handleRemoveToken(item.ticker, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-600/50 text-slate-500 hover:text-rose-400 transition-all"
                  title="Remove from watchlist"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default WatchlistWidget;
